import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import firebase from "gatsby-plugin-firebase";

// Initial state
export const initialState = {
  companyId: "",
  companyName: "",
  website: "",
  photo: "",
  slug: "",
  roles: {},
  isLoading: false,
  hasError: false,
  hasFulfilled: false,
  allCompanies: [],
  allCompaniesFulfilled: [],
};

// Thunks
export const loadCompany = createAsyncThunk(
  "company/loadCompany",
  async (companyId) => {
    try {
      const auth = firebase.auth();
      const db = firebase.firestore();

      const snapshot = await db.collection("companies").doc(companyId).get();
      if (!snapshot.exists) {
        // The company doesn't exist. This should only happen if the admin deletes it.
        // Update the user's account to remove the company
        db.collection("accounts").doc(auth.currentUser.uid).update({
          companies: [], // Assume only one company for now
        });
        return undefined;
      } else {
        const data = snapshot.data();

        // Check to make sure this user is still authorized for this company
        if (
          data.roles &&
          Object.keys(data.roles).includes(auth.currentUser.uid)
        ) {
          // User is authorized
          return { companyId, ...data };
        } else {
          // User is no longer authorized, update account
          db.collection("accounts").doc(auth.currentUser.uid).update({
            companies: [], // Assume only one company for now
          });
          return undefined;
        }
      }
    } catch (error) {
      firebase.analytics().logEvent("load_company", {
        errorCode: error.code,
        errorMsg: error.message,
      });

      return undefined;
    }
  }
);

export const getCompanyById = createAsyncThunk(
  "company/getCompanyById",
  async (companyId) => {
    try {
      const db = firebase.firestore();

      const snapshot = await db.collection("companies").doc(companyId).get();
      if (!snapshot.exists) {
        // The company doesn't exist
        return companyId;
      } else {
        const data = snapshot.data();
        return { companyId, ...data };
      }
    } catch (error) {
      firebase.analytics().logEvent("get_company_by_id", {
        errorCode: error.code,
        errorMsg: error.message,
      });

      return companyId;
    }
  }
);

// Slice
export const companySlice = createSlice({
  name: "company",
  initialState: initialState,
  reducers: {
    setCompany: (state, action) => action.payload,
  },
  extraReducers: {
    [loadCompany.pending]: (state, action) => {
      state.isLoading = true;
      state.hasError = false;
      state.hasFulfilled = false;
    },
    [loadCompany.fulfilled]: (state, action) => {
      if (typeof action.payload === "object") {
        state.companyId = action.payload.companyId;
        state.companyName = action.payload.companyName;
        state.website = action.payload.website;
        state.photo = action.payload.photo;
        state.slug = action.payload.slug;
        state.roles = action.payload.roles;
      }
      state.isLoading = false;
      state.hasError = false;
      state.hasFulfilled = true;
    },
    [loadCompany.rejected]: (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.hasFulfilled = true;
    },
    [getCompanyById.fulfilled]: (state, action) => {
      if (typeof action.payload === "object") {
        state.allCompanies.push(action.payload);
        state.allCompaniesFulfilled.push(action.payload.companyId);
      } else {
        // Snapshot was empty or error occurred, returned companyId
        state.allCompaniesFulfilled.push(action.payload); // companyId
      }
    },
  },
});

// Selectors
export const selectCompany = (state) => state.company;
export const selectCompanyById = (state, companyId) =>
  state.company.allCompanies.find((company) => company.companyId === companyId);

// Exports
export const { setCompany } = companySlice.actions;
export default companySlice.reducer;
